import React from 'react';
import { format, isSameMonth, isToday } from 'date-fns';
import {
  Lesson,
  DailyRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import { CalendarEvent } from '@/hooks';
import useDailyRosterStore from '@/stores/dailyRosterStore';
import useInstructorRosterStore from '@/stores/instructorRosterStore';

interface CalendarDayProps {
  day: Date;
  currentMonth: Date;
  events: CalendarEvent[];
  onLessonClick?: (lesson: Lesson, instructor: DailyRosterInstructor) => void;
  onDayClick?: (date: Date) => void;
  isInstructor?: boolean;
  tomorrow?: Date;
}

const CalendarDay: React.FC<CalendarDayProps> = ({
  day,
  currentMonth,
  events,
  onLessonClick,
  onDayClick,
  isInstructor = false,
  tomorrow = new Date()
}) => {
  const isCurrentMonth = isSameMonth(day, currentMonth);
  const isCurrentDay = isToday(day);

  const isTomorrow =
    day.getDate() === tomorrow.getDate() &&
    day.getMonth() === tomorrow.getMonth() &&
    day.getFullYear() === tomorrow.getFullYear();

  const isDisabled = isInstructor && !isCurrentDay && !isTomorrow;
  const { selectedDate, setSelectedDate, setSelectedType } =
    useDailyRosterStore();
  const {
    setSelectedDate: setSelectedDateInstructor,
    setSelectedType: setSelectedTypeInstructor
  } = useInstructorRosterStore();

  return (
    <div
      className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
        isDisabled
          ? 'bg-gray-100 cursor-not-allowed opacity-50'
          : isCurrentMonth
            ? 'bg-white cursor-pointer hover:bg-slate-50'
            : 'bg-gray-50'
      } transition-colors duration-200`}
      onClick={() => {
        if (isDisabled) return;
        const yyyyMmDd = format(day, 'yyyy-MM-dd');
        if (selectedDate !== yyyyMmDd) {
          setSelectedDate(yyyyMmDd);
          setSelectedDateInstructor(yyyyMmDd);
        }
        setSelectedType('Day');
        setSelectedTypeInstructor('Day');
        if (onDayClick) onDayClick(day);
      }}
    >
      {/* Day Number */}
      <div className='flex items-center justify-between mb-1'>
        <span
          className={`text-sm font-medium ${
            isCurrentMonth
              ? isCurrentDay
                ? 'text-blue-600 bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center'
                : 'text-gray-900'
              : 'text-gray-400'
          }`}
        >
          {format(day, 'd')}
        </span>
      </div>

      {/* Events */}
      <div className='space-y-1'>
        {events.slice(0, 4).map(event => (
          <div
            key={event.id}
            className={`text-xs p-1.5 rounded border cursor-pointer transition-all duration-200 ${getEventTypeColor(
              event.type
            )}`}
            onClick={e => {
              if (isDisabled) {
                e.stopPropagation();
                return;
              }
              if (event.lesson && event.instructor && onLessonClick) {
                onLessonClick(event.lesson, event.instructor);
              }
            }}
            title={`${event.title} - ${event.instructor?.name || 'Unknown'}`}
          >
            <div className='font-medium truncate leading-tight'>
              {getEventDisplayText(event)}
            </div>
          </div>
        ))}
        {events.length > 4 && (
          <div className='text-xs text-gray-500 text-center py-1'>
            +{events.length - 4} more
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
const getEventTypeColor = (type: string) => {
  switch (type) {
    case 'group':
      return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
    case 'private':
      return 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100';
    case 'time-off':
      return 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
    default:
      return 'bg-gray-50 text-slate-700 border-gray-200 hover:bg-gray-100';
  }
};

const getEventDisplayText = (event: CalendarEvent) => {
  // For monthly view, use the title directly if it's descriptive
  if (event.title && event.title !== 'Lesson') {
    // Check if it's a group lesson and format accordingly
    if (event.type === 'group') {
      if (event.title.includes('Kids')) {
        if (event.title.includes('AM')) return 'Group-Kids Group Lesson AM';
        if (event.title.includes('PM')) return 'Group-Kids Group Lesson PM';
        if (event.title.includes('Full Day') || event.title.includes('FD'))
          return 'Group-Kids Group Lesson Full Day';
        return 'Group-Kids Group Lesson';
      } else if (event.title.includes('Adults')) {
        if (event.title.includes('AM')) return 'Group-Adults Group Lesson AM';
        if (event.title.includes('PM')) return 'Group-Adults Group Lesson PM';
        if (event.title.includes('Full Day') || event.title.includes('FD'))
          return 'Group-Adults Group Lesson Full Day';
        return 'Group-Adults Group Lesson';
      }
      return `Group-${event.title}`;
    } else if (event.type === 'time-off') {
      if (event.title.includes('Half Day') || event.title.includes('OHD'))
        return 'Group-Time Off Half Day';
      return 'Group-Time Off';
    }

    return event.title;
  }

  // Fallback to lesson code for daily view
  const lessonCode = event.lesson?.lesson_code?.code || '';

  if (lessonCode.includes('Group')) {
    if (lessonCode.includes('Kids')) {
      if (lessonCode.includes('AM')) return 'Group-Kids Group Lesson AM';
      if (lessonCode.includes('PM')) return 'Group-Kids Group Lesson PM';
      if (lessonCode.includes('Full Day') || lessonCode.includes('FD'))
        return 'Group-Kids Group Lesson Full Day';
      return 'Group-Kids Group Lesson';
    } else if (lessonCode.includes('Adults')) {
      if (lessonCode.includes('AM')) return 'Group-Adults Group Lesson AM';
      if (lessonCode.includes('PM')) return 'Group-Adults Group Lesson PM';
      if (lessonCode.includes('Full Day') || lessonCode.includes('FD'))
        return 'Group-Adults Group Lesson Full Day';
      return 'Group-Adults Group Lesson';
    }
    return `Group-${lessonCode}`;
  } else if (lessonCode.includes('Time Off') || lessonCode.includes('T-OFF')) {
    if (lessonCode.includes('Half Day') || lessonCode.includes('OHD'))
      return 'Group-Time Off Half Day';
    return 'Group-Time Off';
  } else if (lessonCode.includes('Early Bird')) {
    return 'Group-Early Bird Private 2.5 hours';
  }

  return lessonCode || 'Lesson';
};

export default CalendarDay;
